import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, ShoppingBag, Package, BarChart3, Zap, IndianRupee } from "lucide-react";

/**
 * A fully custom, dependency-free premium dashboard mockup for the hero.
 * No stock illustrations. All SVG, all animated with Framer Motion.
 */

const Sparkline = () => {
  // deterministic, tidy uptrending line
  const pts = [4, 8, 6, 11, 9, 14, 13, 18, 20, 23, 22, 28, 32];
  const max = Math.max(...pts);
  const w = 260;
  const h = 60;
  const stepX = w / (pts.length - 1);
  const path = pts
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * stepX},${h - (p / max) * (h - 8)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16">
      <defs>
        <linearGradient id="sl" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${path} L ${w},${h} L 0,${h} Z`}
        fill="url(#sl)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      />
      <motion.path
        d={path}
        fill="none"
        stroke="#059669"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      />
      {pts.map((p, i) => (
        <motion.circle
          key={i}
          cx={i * stepX}
          cy={h - (p / max) * (h - 8)}
          r={i === pts.length - 1 ? 3.5 : 0}
          fill="#84cc16"
          stroke="#fff"
          strokeWidth="2"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1.4 }}
        />
      ))}
    </svg>
  );
};

const Bars = () => {
  const bars = [40, 55, 32, 72, 60, 84, 78];
  return (
    <div className="flex items-end gap-1.5 h-14">
      {bars.map((b, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: `${b}%` }}
          transition={{ delay: 0.2 + i * 0.06, duration: 0.6, ease: "easeOut" }}
          className="w-3 rounded-md bg-gradient-to-t from-emerald-500/70 to-lime-400"
          style={{ boxShadow: "0 6px 16px -6px rgba(16,185,129,0.5)" }}
        />
      ))}
    </div>
  );
};

const Donut = ({ pct = 72 }) => {
  const r = 28;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 72 72" className="w-full h-full -rotate-90">
        <circle cx="36" cy="36" r={r} stroke="#e5e7eb" strokeWidth="7" fill="none" />
        <motion.circle
          cx="36"
          cy="36"
          r={r}
          stroke="#059669"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - (pct / 100) * c }}
          transition={{ duration: 1.4, ease: "easeOut", delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center font-display font-bold text-slate-900">
        {pct}%
      </div>
    </div>
  );
};

const Product = ({ hue, name, price }) => (
  <div className="flex items-center gap-2.5 p-2 rounded-xl bg-slate-50/70 border border-slate-100">
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center"
      style={{ background: `linear-gradient(135deg, ${hue}, #ffffff40)` }}
    >
      <ShoppingBag className="w-4 h-4 text-slate-800" />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] font-semibold text-slate-800 truncate">{name}</div>
      <div className="text-[10px] text-slate-500 font-mono">₹{price}</div>
    </div>
  </div>
);

const Card = ({ children, className = "", delay = 0, style }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
    className={`glass-light rounded-2xl shadow-premium p-4 ${className}`}
    style={style}
  >
    {children}
  </motion.div>
);

const PremiumDashboard = () => {
  return (
    <div className="relative w-full max-w-[560px] mx-auto lg:ml-auto lg:mr-0">
      {/* Aurora halo behind */}
      <div
        className="absolute -inset-8 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(50% 50% at 60% 40%, rgba(16,185,129,0.35), transparent 70%), radial-gradient(40% 40% at 20% 80%, rgba(163,230,53,0.4), transparent 70%)",
          filter: "blur(28px)",
        }}
        aria-hidden
      />

      <div className="relative grid grid-cols-6 auto-rows-[64px] gap-3">
        {/* Shopify store card */}
        <Card className="col-span-6" delay={0.05}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white grid place-items-center font-display font-bold">
                S
              </div>
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">
                  Shopify Store
                </div>
                <div className="text-sm font-semibold text-slate-900">boostf-demo.myshopify.com</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-mono text-emerald-700">
              <span className="live-dot" /> Live
            </div>
          </div>
        </Card>

        {/* Revenue */}
        <Card className="col-span-4 row-span-2" delay={0.15}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
              Revenue · 30d
            </div>
            <div className="chip !py-0.5 !text-[10px]">
              <TrendingUp className="w-3 h-3" /> +42%
            </div>
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <IndianRupee className="w-5 h-5 text-slate-900" />
            <div className="font-display font-bold text-3xl sm:text-4xl text-slate-900 tabular-nums">
              4,82,340
            </div>
          </div>
          <Sparkline />
        </Card>

        {/* Orders */}
        <Card className="col-span-2 row-span-2" delay={0.22}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
            Orders
          </div>
          <div className="mt-1 font-display font-bold text-2xl sm:text-3xl text-slate-900">
            1,284
          </div>
          <div className="mt-2 flex items-center gap-1 text-xs text-emerald-700 font-mono">
            <Package className="w-3.5 h-3.5" /> 128 today
          </div>
          <div className="mt-2">
            <Bars />
          </div>
        </Card>

        {/* Meta ads */}
        <Card className="col-span-3 row-span-2" delay={0.3}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
              Meta Ads · ROAS
            </div>
            <Zap className="w-3.5 h-3.5 text-lime-500" />
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <div className="font-display font-bold text-3xl text-slate-900">3.8</div>
            <div className="text-xs text-slate-500">x</div>
          </div>
          <div className="mt-2 space-y-1.5">
            {[
              { label: "Creative A", val: 82 },
              { label: "Creative B", val: 64 },
              { label: "Creative C", val: 48 },
            ].map((r, i) => (
              <div key={r.label} className="flex items-center gap-2">
                <div className="text-[10px] text-slate-500 w-16">{r.label}</div>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${r.val}%` }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.7 }}
                    className="h-full bg-gradient-to-r from-emerald-500 to-lime-400"
                  />
                </div>
                <div className="text-[10px] font-mono text-slate-600 w-8 text-right">
                  {r.val}%
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Analytics donut */}
        <Card className="col-span-3 row-span-2" delay={0.38}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
            Store Analytics
          </div>
          <div className="mt-1 flex items-center gap-3">
            <Donut pct={72} />
            <div className="text-xs space-y-1">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-600" /> Converted
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-slate-200" /> Abandoned
              </div>
              <div className="mt-1.5 flex items-center gap-1 text-emerald-700 font-mono">
                <BarChart3 className="w-3 h-3" /> +18% MoM
              </div>
            </div>
          </div>
        </Card>

        {/* Products */}
        <Card className="col-span-6 row-span-2" delay={0.45}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">
              Winning Products
            </div>
            <div className="text-[11px] font-mono text-slate-500">7 active</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Product hue="#a7f3d080" name="Aroma Diffuser" price="1,299" />
            <Product hue="#d9f99d80" name="Posture Belt" price="899" />
            <Product hue="#bbf7d080" name="LED Lamp" price="1,499" />
          </div>
        </Card>
      </div>

      {/* Floating badge */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="hidden sm:flex float-slow absolute -left-4 -bottom-4 items-center gap-2 pl-2 pr-3 py-1.5 rounded-full glass-light shadow-premium"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-lime-400 grid place-items-center text-white text-xs font-bold">
          ✓
        </div>
        <div className="text-xs">
          <div className="font-semibold text-slate-900 leading-tight">First sale unlocked</div>
          <div className="text-[10px] text-slate-500 font-mono">2 minutes ago</div>
        </div>
      </motion.div>
    </div>
  );
};

export default PremiumDashboard;
